"use client";

import { useEffect, useRef } from "react";
import posthog from "posthog-js";
import { DIRECTORY_FILTER_EVENT, type ActiveDirectoryFilters, type DirectoryFilterSurface } from "@/lib/directory-reporting-events";

interface Props {
  orgIdentifier?: string;
  surface: DirectoryFilterSurface;
  routeTemplate: string;
  activeFilters: ActiveDirectoryFilters;
  resultCount: number;
  sort?: string;
  view?: "map" | "list";
  agentId?: string;
}

/** Record applied state, including a reset. Pagination and ordinary rerenders are not filter changes. */
export function DirectoryFilterTelemetry({ orgIdentifier, surface, routeTemplate, activeFilters, resultCount, sort, view, agentId }: Props) {
  const previous = useRef<string | null>(null);
  const filters = Object.fromEntries(Object.entries(activeFilters)
    .filter(([, value]) => Array.isArray(value) ? value.length > 0 : Boolean(value))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => [key, Array.isArray(value) ? [...value].sort() : value]));
  const stateKey = JSON.stringify({ orgIdentifier, surface, routeTemplate, filters, sort, agentId });

  useEffect(() => {
    if (!orgIdentifier || !process.env.NEXT_PUBLIC_POSTHOG_KEY || previous.current === stateKey) return;
    const snapshotType = previous.current === null ? "initial_state" : "filter_change";
    previous.current = stateKey;
    try {
      posthog.capture(DIRECTORY_FILTER_EVENT, {
        app: "agentdirectory",
        org_id: orgIdentifier,
        surface,
        route_template: routeTemplate,
        active_filters: filters,
        active_filter_count: Object.keys(filters).length,
        result_count: resultCount,
        snapshot_type: snapshotType,
        ...(sort ? { sort } : {}),
        ...(view ? { view } : {}),
        ...(agentId ? { agent_id: agentId } : {}),
      });
    } catch {
      // Reporting cannot interrupt directory use.
    }
  });

  return null;
}
